import { Link } from 'react-router-dom';
import type { Project } from '../../lib/projects';
import { PinButton } from './PinButton';
import { StatusBadge } from './StatusBadge';
import { ProjectThumb } from './ProjectThumb';

/**
 * A pinned project in the dashboard's sidebar list.
 *
 * A slimmer sibling of ProjectRow rather than a reuse of it: ProjectRow carries
 * a description, tag pills and an updated-at date, and its `md:` breakpoint is
 * viewport-based -- so in a one-third-width column those columns would all
 * still render, into a third of the space they were measured for.
 *
 * Same arrangement ProjectRow documents: the <li> carries the hover treatment,
 * so the pin button, which has to sit outside the anchor, still reads as part
 * of the same row.
 */
export function PinnedProjectRow({
  project,
  onTogglePin,
  pinning = false,
}: {
  project: Project;
  /** Omitted where there is nothing to update, e.g. a read-only listing. */
  onTogglePin?: (project: Project) => void;
  pinning?: boolean;
}) {
  return (
    <li className="flex items-center transition hover:bg-stone-100/70">
      <Link
        to={`/projects/${project.id}`}
        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2"
      >
        <ProjectThumb project={project} className="h-8 w-8 text-xs" />

        <p className="min-w-0 flex-1 truncate text-sm font-medium text-stone-900">{project.name}</p>

        <div className="shrink-0">
          <StatusBadge status={project.status} />
        </div>
      </Link>

      {onTogglePin ? (
        <div className="shrink-0 pr-2">
          <PinButton pinned={project.pinned} busy={pinning} onClick={() => onTogglePin(project)} />
        </div>
      ) : null}
    </li>
  );
}
